import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { 
  Box,
  Stack,
  HStack,
  Button,
  useToast 
} from '@chakra-ui/react';
import { getLibrariesData, updateLibrariesData } from '../../../services/Libraries';
import Form from '@rjsf/chakra-ui';
import validator from "@rjsf/validator-ajv8";
import FormLoader from '../../../CommonComponents/FormLoader';
import { useSelector } from 'react-redux';
import { handleApiError, showSuccessToast } from '../../../util/errorHandler';

const Dimensions = () => {

  const { alias } = useParams();
  const [quickSave, setQuickSave] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [leftFormData, setLeftFormData] = useState({});
  const [rightFormData, setRightFormData] = useState({});
  const [combinedFormData, setCombinedFormData] = useState({});
  const previewEnabled = useSelector(state => state.preview.isEnabled);
  const toast = useToast();
  const type=window.location.pathname.split('/')[1];

  // Split data for two-column layout
  const splitFormData = (data = {}) => {
    const leftFields = ['_id', 'height_cm', 'height_in', 'image_width', 'image_height'];
    const rightFields = ['width_cm', 'width_in', 'image_orientation'];
    
    const leftData = {};
    const rightData = {};
    
    leftFields.forEach(field => {
      if (data.hasOwnProperty(field)) {
        leftData[field] = data[field];
      }
    });
    
    rightFields.forEach(field => {
      if (data.hasOwnProperty(field)) {
        rightData[field] = data[field];
      }
    });
    
    return { leftData, rightData };
  };
  
  // Combine data from two forms
  const combineFormData = (leftData, rightData) => {
    return { ...leftData, ...rightData };
  };

  // Form change handlers
  const handleLeftFormChange = (data) => {
    setLeftFormData(data.formData);
  };

  const handleRightFormChange = (data) => {
    setRightFormData(data.formData);
  };

  const handleCombinedFormChange = (data) => {
    setCombinedFormData(data.formData);
  };

  // Left column schema
  const leftColumnSchema = {
    "title": "Height Dimensions",
    "type": "object",
    "properties": {
      "_id": { "type": "string" },
      "height_cm": {
        "type": "string",
        "title": "Height (cm)"
      },
      "height_in": {
        "type": "string",
        "title": "Height (in)"
      },
      "image_width": {
        "type": "number",
        "title": "Image Width"
      },
      "image_height": {
        "type": "number",
        "title": "Image Height"
      }
    }
  };

  // Right column schema
  const rightColumnSchema = {
    "title": "Width Dimensions",
    "type": "object",
    "properties": {
      "width_cm": {
        "type": "string",
        "title": "Width (cm)"
      },
      "width_in": {
        "type": "string",
        "title": "Width (in)"
      },
      "image_orientation": {
        "type": "string",
        "title": "Image Orientation",
        "readOnly": true
      }
    }
  };

  // Combined schema for preview mode
  const combinedSchema = {
    "title": "Dimensions Fields",
    "description": "",
    "type": "object",
    "properties": {
      "_id": { "type": "string" },
      "height_cm": {
        "type": "string",
        "title": "Height (cm)"
      },
      "height_in": {
        "type": "string",
        "title": "Height (in)"
      },
      "width_cm": {
        "type": "string",
        "title": "Width (cm)"
      },
      "width_in": {
        "type": "string",
        "title": "Width (in)"
      },
      "image_width": {
        "type": "number",
        "title": "Image Width"
      },
      "image_height": {
        "type": "number",
        "title": "Image Height"
      },
      "image_orientation": {
        "type": "string",
        "title": "Image Orientation",
        "readOnly": true
      }
    }
  };

  // Left column UI schema
  const leftColumnUiSchema = {
    "ui:submitButtonOptions": { "norender": true },
    "_id": { "ui:widget": "hidden" },
    "ui:style": {
      fontSize: "14px",
    },
    height_cm: { "ui:emptyValue": "" },
    height_in: { "ui:emptyValue": "" },
    image_width: { "ui:emptyValue": "" },
    image_height: { "ui:emptyValue": "" }
  };

  // Right column UI schema
  const rightColumnUiSchema = {
    "ui:submitButtonOptions": { "norender": true },
    "ui:style": {
      fontSize: "14px",
    },
    width_cm: { "ui:emptyValue": "" },
    width_in: { "ui:emptyValue": "" },
    image_orientation: { "ui:emptyValue": "" }
  };

  // Combined UI schema for preview mode
  const combinedUiSchema = {
    "ui:submitButtonOptions": { "norender": true },
    "_id": { "ui:widget": "hidden" },
    "ui:style": {
      fontSize: "14px",
    },
    height_cm: { "ui:emptyValue": "" },
    height_in: { "ui:emptyValue": "" },
    width_cm: { "ui:emptyValue": "" },
    width_in: { "ui:emptyValue": "" },
    image_width: { "ui:emptyValue": "" },
    image_height: { "ui:emptyValue": "" },
    image_orientation: { "ui:emptyValue": "" }
  };

  // Fetch initial data
  useEffect(() => {
    const getBasicData = async () => {
      try {
        if (alias) {
          const result = await getLibrariesData(alias, 'dimensions', type);

          if (result) {
            const data = result?.data?.[0];
            // Split data for two-column layout
            const { leftData, rightData } = splitFormData(data);
            setLeftFormData(leftData);
            setRightFormData(rightData);
            setCombinedFormData(data);
            setQuickSave(false);
          }
        } else {
          // For new records, initialize empty forms
          const emptyData = {};
          const { leftData, rightData } = splitFormData(emptyData);
          setLeftFormData(leftData);
          setRightFormData(rightData);
          setCombinedFormData(emptyData);
        }
      } catch (error) {
        handleApiError(error, toast, {
          defaultErrorMessage: "Failed to fetch data"
        });
      } finally {
        setIsLoading(false);
      }
    };

    getBasicData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alias, toast, quickSave]);

  // Submit updated data
  const handleSubmit = async () => {
    setIsSubmitting(true);
    
    try {
      let finalFormData;
      
      if (previewEnabled) {
        finalFormData = combinedFormData;
      } else {
        finalFormData = combineFormData(leftFormData, rightFormData);
      }
      
      // Remove _id from create operations
      if (!alias && finalFormData._id) {
        delete finalFormData._id;
      }

      if (alias) {
        const result = await updateLibrariesData(alias, finalFormData, "", 'dimensions');

        if (result?.status === 200) {
          setQuickSave(prev => !prev);
          showSuccessToast(toast, "Data updated successfully");
        }
      }
    } catch (error) {
      handleApiError(error, toast, {
        defaultErrorMessage: "Failed to update data"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box maxW="auto" pb="100px">
      {isLoading ? (
        <FormLoader />
      ) : (
        <Box>
          {previewEnabled ? (
            /* Single column layout for preview mode */
            <Box display={"flex"} flexDirection={"row"} alignItems={"flex-start"}>
              <Box flex={1} bg="white" border="1px solid" borderColor="gray.200" p={4} borderRadius="md">
                <Box fontSize="lg" fontWeight="bold" mb={4}>Dimensions Fields</Box>
                <Form
                  schema={combinedSchema}
                  uiSchema={combinedUiSchema}
                  formData={combinedFormData}
                  validator={validator}
                  onChange={handleCombinedFormChange}
                  fontsize={"14px"}
                />
              </Box>
            </Box>
          ) : (
            /* Two column layout with separate forms */
            <Box display={"flex"} flexDirection={"row"} alignItems={"flex-start"}>
              <Stack flexDirection="row" gap={4} alignItems="flex-start" flex={1}>
                <HStack alignItems="flex-start" flex={1}>
                  {/* Left Column */}
                  <Box flex={1} bg="white" border="1px solid" borderColor="gray.200" p={4} borderRadius="md">
                    <Box fontSize="lg" fontWeight="bold" mb={4}>Dimensions</Box>
                    <Form
                      schema={leftColumnSchema}
                      uiSchema={leftColumnUiSchema}
                      formData={leftFormData}
                      validator={validator}
                      onChange={handleLeftFormChange}
                      fontsize={"14px"}
                    />
                  </Box>
                  {/* Right Column */}
                  <Box flex={1} bg="white" border="1px solid" borderColor="gray.200" p={4} borderRadius="md">
                    <Form
                      schema={rightColumnSchema}
                      uiSchema={rightColumnUiSchema}
                      formData={rightFormData}
                      validator={validator}
                      onChange={handleRightFormChange}
                      fontsize={"14px"}
                    />
                  </Box>
                </HStack>
              </Stack>
            </Box>
          )}
        </Box>
      )}
      
      {/* Fixed Save Button */}
      <Box
        position="fixed"
        bottom={0}
        left={"250px"}
        right={0}
        bg="white"
        borderTop="1px solid"
        borderColor="gray.200"
        p={4}
        zIndex={1000}
      >
        <Box maxW="auto" mx="auto" display="flex" justifyContent="center">
          <Button
            colorScheme="blue"
            onClick={handleSubmit}
            isLoading={isSubmitting}
            width="auto"
            margin="0 auto"
            textAlign="center"
          >
            {isSubmitting ? 'Saving...' : 'Save'}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default Dimensions;